import { api } from '@api/api';
import { ContentAreaModel } from '@api/types';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { ThemeIds } from '@themes/theme.types';
import { themes } from '@themes/themes';
import { isContentAreaEqual } from '@utils/content.utils';

// INITIAL STATE

type CachedContentArea = {
  data?: ContentAreaModel;
  isLoaded?: boolean;
};

export type SystemPages = {
  buyersCertificateCreatePage?: string;
  buyersCertificateConsentPage?: string;
  dealerBuyersCertificateListPage?: string;
  beremaBuyersCertificateListPage?: string;
  buyersCertificateDetailsPage?: string;
  cartPage?: string;
  checkoutPage?: string;
  errorPage?: string;
  homePage?: string;
  homePageGuid?: string;
  homePageName?: string;
  notFoundPage?: string;
  forbiddenPage?: string;
  loginPage?: string;
  forgotPasswordPage?: string;
  resetPasswordPage?: string;
  orderDetailsPage?: string;
  searchResultsPage?: string;
  dealerDetailsPage?: string;
  dealerTechnicalMessageListPage?: string;
  beremaTechnicalMessageListPage?: string;
  technicalMessageCreatePage?: string;
  warrantyCreatePage?: string;
  warrantyDetailsPage?: string;
  dealerWarrantyListPage?: string;
  beremaWarrantyListPage?: string;
  findDealerPage?: string;
  technicalMessageDetailsPage?: string;
  googleMapApiKey?: string;
  googleGeoServiceApiKey?: string;
  confirmationPage?: string;
  productOrderRequestDetailsPage?: string;
  productInfoRequestDetailsPage?: string;
  productOrderRequestListPage?: string;
  productInfoRequestListPage?: string;
  smsServiceApiPassword?: string;
  privacyPolicyPage?: string;
  termsAndConditionsPage?: string;
  disableOrderProcessing?: boolean;
  disableOrderMessage?: string;
};
export interface AppState {
  theme: ThemeIds;
  currentPageGuid?: string;
  currentPageName?: string;
  currentPageDisplayName?: string;
  currentPageContentType?: string[];
  itemERPDescription?: string;
  header: CachedContentArea;
  afterHeader: CachedContentArea;
  footer: CachedContentArea;
  systemPages: SystemPages;
}

const emptyCachedContentArea = {
  isLoaded: false,
  data: [],
};

const initialState: AppState = {
  theme: ThemeIds.Light,
  header: emptyCachedContentArea,
  afterHeader: emptyCachedContentArea,
  footer: emptyCachedContentArea,
  systemPages: {},
};

const updateCachedArea = (oldArea?: CachedContentArea, newArea?: ContentAreaModel) => {
  return {
    isLoaded: true,
    data: !isContentAreaEqual(oldArea?.data, newArea) ? newArea : oldArea?.data,
  };
};

// REDUCERS
export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setCurrentThemeId: (state, action: PayloadAction<ThemeIds>) => {
      state.theme = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Site Settings
    builder.addMatcher(api.endpoints.getSettings.matchFulfilled, (state, action) => {
      state.systemPages = action.payload.data as SystemPages;
    });

    // Cache Header and Footer data in store
    // Runs when new page data is loaded
    // Update header and footer only if they differ from the cached ones
    builder.addMatcher(api.endpoints.getPageByUrl.matchFulfilled, (state, action) => {
      const page = action.payload.at(0);
      state.currentPageGuid = page?.contentLink?.guidValue;
      state.currentPageName = page?.name;
      state.currentPageDisplayName = page?.displayName;
      state.currentPageContentType = page?.contentType;
      state.header = updateCachedArea(state.header, page?.headerContent);
      state.afterHeader = updateCachedArea(state.afterHeader, page?.afterHeaderContent);
      state.footer = updateCachedArea(state.footer, page?.footerContent);
      state.itemERPDescription = page?.itemERPDescription;
    });
  },
});

const selectAppState = (state: RootState) => state.app;

// ACTIONS
export const { setCurrentThemeId } = appSlice.actions;

// Theme selectors
//-----------------
export const getCurrentThemeId = createSelector(
  (state: RootState) => state.app.theme,
  (theme) => theme
);

export const getCurrentTheme = createSelector(
  (state: RootState) => state.app.theme,
  (themeId) => themes.find((theme) => theme.value === themeId)?.theme || themes[0].theme
);

// Cached content areas
//----------------------
export const getHeaderContentArea = createSelector(
  (state: RootState) => state.app.header.data,
  (header) => header
);

export const isHeaderContentLoaded = createSelector(
  (state: RootState) => state.app.header.isLoaded,
  (isLoaded) => isLoaded
);

export const getAfterHeaderContentArea = createSelector(
  (state: RootState) => state.app.afterHeader.data,
  (afterHeader) => afterHeader
);

export const isAfterHeaderContentLoaded = createSelector(
  (state: RootState) => state.app.afterHeader.isLoaded,
  (isLoaded) => isLoaded
);

export const getFooterContentArea = createSelector(
  (state: RootState) => state.app.footer.data,
  (footer) => footer
);

export const isFooterContentLoaded = createSelector(
  (state: RootState) => state.app.footer.isLoaded,
  (isLoaded) => isLoaded
);

export const getCurrentPageId = createSelector(
  (state: RootState) => state.app.currentPageGuid,
  (guid) => guid
);

export const getItemERPDescription = createSelector(
  (state: RootState) => state.app.itemERPDescription,
  (guid) => guid
);

export const getCurrentPageName = createSelector(
  (state: RootState) => state.app.currentPageName,
  (name) => name
);

export const getCurrentPageDisplayName = createSelector(
  (state: RootState) => state.app.currentPageDisplayName,
  (displayName) => displayName
);

export const getCurrentPageContentType = createSelector(
  (state: RootState) => state.app.currentPageContentType,
  (contentType) => contentType
);

export const getSystemPages = createSelector(
  selectAppState,
  (systemPage) => systemPage.systemPages
);

// System pages
//--------------
export const getSpecialPage = (pageType: keyof SystemPages) =>
  createSelector(
    (state: RootState) => state.app.systemPages?.[pageType],
    (page) => page as string
  );

export default appSlice.reducer;
